import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { appVersion } from '../lib/app-version';

const reloadPage = () => window.location.reload();

/**
 * Turns the next in-app navigation into a full page load once a newer bundle is
 * deployed. Leaving a page already discards its form state, so the reload costs
 * the user nothing, and it lands the tab on the current deploy before it can send
 * an old request shape or ask for a chunk that no longer exists.
 *
 * Only a navigation triggers it, never a re-render: a tab found stale while the
 * user is mid-form keeps their input until they move on.
 *
 * A navigation carrying router `state` is skipped: a page load drops that state,
 * and the page it was meant for would render without it. The next plain
 * navigation catches the tab instead.
 */
export function useReloadOnNavigationWhenStale(reload: () => void = reloadPage): void {
  const { key, state } = useLocation();
  const lastKeyRef = useRef(key);

  useEffect(() => {
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    if (state != null) return;
    if (appVersion.isStale()) reload();
  }, [key, state, reload]);
}
